import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  getAuth,
  onAuthStateChanged,
  User,
  signOut,
} from 'firebase/auth';
import { StaffUser, Role } from '../types/staff';
import {
  ensureStaffUserForAuth,
  getStaffUser,
  claimPendingInvitation,
} from '../services/staff';

const PENDING_INVITE_TOKEN_KEY = 'huduma_pending_invite_token';

interface AuthContextType {
  currentUser: User | null;
  staffUser: StaffUser | null;
  role: Role | null;
  loading: boolean;
  /** True if the auth user has no corresponding `users/{uid}` doc and isn't the hardcoded admin. */
  unauthorized: boolean;
  logout: () => Promise<void>;
  /** Re-read the signed-in user's own `users/{uid}` doc into state (e.g. to pick
   *  up an admin-recorded staff pay total without a full re-login). */
  refreshStaffUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  staffUser: null,
  role: null,
  loading: true,
  unauthorized: false,
  logout: async () => {},
  refreshStaffUser: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [staffUser, setStaffUser] = useState<StaffUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setUnauthorized(false);

      if (!user) {
        setStaffUser(null);
        setLoading(false);
        return;
      }

      try {
        let su = await ensureStaffUserForAuth(
          user.uid,
          user.email,
          user.displayName,
        );

        // If the doc doesn't exist yet, we might be in the middle of an
        // invitation-acceptance flow: `acceptInvitation` calls
        // `createUserWithEmailAndPassword` which fires this listener BEFORE
        // it has had a chance to write the `users/{uid}` doc. Signing the
        // user out here would also cancel their auth session, causing the
        // subsequent setDoc inside acceptInvitation to fail with
        // permission-denied. Poll briefly to let the write land.
        if (!su) {
          // Recovery path 1: if AcceptInvite stashed a pending invitation
          // token in sessionStorage just before sign-up, claim it here.
          // This makes the flow resilient to races and to orphan-account
          // recovery (when the user already had an Auth account from a
          // prior broken attempt and just signed back in via signIn).
          const pendingToken = sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY);
          if (pendingToken) {
            console.log('[AuthContext] claiming pending invitation', pendingToken);
            const claimed = await claimPendingInvitation(
              pendingToken,
              user.uid,
              user.email,
            );
            sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
            if (claimed) {
              console.log('[AuthContext] invitation claimed successfully');
              su = claimed;
            } else {
              console.warn('[AuthContext] could not claim invitation; falling back to polling');
            }
          }

          // Recovery path 2: poll briefly to give `acceptInvitation`'s
          // own setDoc time to land if that path is still in flight.
          if (!su) {
            const POLL_INTERVAL_MS = 500;
            const POLL_MAX = 12; // ≈6 s total
            console.log('[AuthContext] no users/{uid} doc on first read; polling…', {
              uid: user.uid,
              email: user.email,
            });
            for (let i = 0; i < POLL_MAX; i++) {
              await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
              const retry = await getStaffUser(user.uid);
              if (retry) {
                console.log('[AuthContext] poll found doc after', i + 1, 'tries');
                su = retry;
                break;
              }
            }
          }
        }

        if (!su) {
          // Still no doc after the grace window. Auth account exists but
          // it's not the bootstrap admin and no acceptance flow completed.
          // Block and sign out.
          console.warn(
            '[AuthContext] no users/{uid} doc after polling — signing out.',
            { uid: user.uid, email: user.email },
          );
          setUnauthorized(true);
          setStaffUser(null);
          await signOut(auth);
          setLoading(false);
          return;
        }

        if (su.disabled) {
          setUnauthorized(true);
          setStaffUser(null);
          await signOut(auth);
          setLoading(false);
          return;
        }

        setStaffUser(su);
      } catch (err) {
        console.error('Failed to load staff user doc:', err);
        setStaffUser(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const logout = async () => {
    const auth = getAuth();
    await signOut(auth);
  };

  const refreshStaffUser = async () => {
    const auth = getAuth();
    const u = auth.currentUser;
    if (!u) return;
    try {
      const su = await getStaffUser(u.uid);
      // Only apply an active doc; a disabled/removed doc is handled by the
      // auth listener on the next token refresh, not here.
      if (su && !su.disabled) setStaffUser(su);
    } catch (err) {
      console.error('refreshStaffUser failed:', err);
    }
  };

  const value: AuthContextType = {
    currentUser,
    staffUser,
    role: staffUser?.role ?? null,
    loading,
    unauthorized,
    logout,
    refreshStaffUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
